import {
  CopyIcon,
  CounterClockwiseClockIcon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  IconButton,
  Link,
  Select,
  Switch,
  Table,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import env from 'config';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FeatureFlagDuplicateDialog } from 'components/platform-console/sections/feature-flags/duplicate-dialog';
import { differenceInCalendarDays, lightFormat, parseISO } from 'date-fns';
import { FeatureFlagHelper } from 'lib_ts/classes/feature-flag.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import {
  FEATURE_FLAG_ENVIRONMENT_OPTIONS,
  FeatureFlagEnvironment,
} from 'lib_ts/enums/feature-flags.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IFeatureFlag } from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import { PlatformConsoleService } from 'services/platform-console.service';
import { useAuthStore } from 'stores/use-auth-store';

const OWNER_FILTER_ALL = 'all';

const DEFAULT_ENVIRONMENT =
  FeatureFlagHelper.mapRuntimeToEnvironment(env.identifier) ??
  FeatureFlagEnvironment.Dev;

/** environments this deployment is allowed to manage (DB-access scoped) */
const MANAGEABLE_ENVIRONMENTS = FeatureFlagHelper.getManageableEnvironments(
  env.identifier
);

const MANAGEABLE_ENVIRONMENT_OPTIONS = FEATURE_FLAG_ENVIRONMENT_OPTIONS.filter(
  (o) => MANAGEABLE_ENVIRONMENTS.includes(o.value as FeatureFlagEnvironment)
);

/** duplicating needs at least one manageable environment other than the source */
const CAN_DUPLICATE = MANAGEABLE_ENVIRONMENTS.length > 1;

/** "n days ago" summary for the last-toggle timestamp */
const daysSince = (iso?: string): string => {
  if (!iso) {
    return '—';
  }

  const days = differenceInCalendarDays(new Date(), parseISO(iso));

  if (days <= 0) {
    return 'Today';
  }

  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const FeatureFlagsSection = () => {
  const navigate = useNavigate();

  const [environment, setEnvironment] =
    useState<FeatureFlagEnvironment>(DEFAULT_ENVIRONMENT);

  const currentUserID = useAuthStore((s) => s.current.userID);

  const [loading, setLoading] = useState(false);
  const [flags, setFlags] = useState<IFeatureFlag[]>([]);

  // client-side filters over the already-loaded environment list
  const [ownerFilter, setOwnerFilter] = useState<string>(OWNER_FILTER_ALL);
  const [myFlagsOnly, setMyFlagsOnly] = useState(false);

  // distinct owner emails present in the loaded flags, for the owner dropdown
  const ownerOptions = useMemo(() => {
    const emails = new Set<string>();

    for (const flag of flags) {
      if (flag.createdBy?.email) {
        emails.add(flag.createdBy.email);
      }
    }

    return Array.from(emails).sort();
  }, [flags]);

  const visibleFlags = useMemo(
    () =>
      flags.filter((flag) => {
        if (myFlagsOnly && !FeatureFlagHelper.isOwnedBy(flag, currentUserID)) {
          return false;
        }

        if (
          ownerFilter !== OWNER_FILTER_ALL &&
          flag.createdBy?.email !== ownerFilter
        ) {
          return false;
        }

        return true;
      }),
    [flags, ownerFilter, myFlagsOnly, currentUserID]
  );

  // dialogs (keyed by timestamp so they remount fresh each open)
  const [dialogDelete, setDialogDelete] = useState<number>();
  const [dialogDuplicate, setDialogDuplicate] = useState<number>();
  const [dialogToggle, setDialogToggle] = useState<number>();
  const [pendingToggle, setPendingToggle] = useState<{
    flag: IFeatureFlag;
    nextEnabled: boolean;
  }>();
  const [selectedFlag, setSelectedFlag] = useState<IFeatureFlag>();

  const refresh = useCallback(async (targetEnv: FeatureFlagEnvironment) => {
    setLoading(true);

    try {
      const result =
        await PlatformConsoleService.getInstance().listFeatureFlags(targetEnv);

      setFlags(result);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(environment);
  }, [environment, refresh]);

  /**
   * Gate the inline toggle behind an ownership warning: owners toggle directly,
   * non-owners confirm first (the Switch is controlled by `flag.enabled`, so it
   * visually reverts on cancel since no state changes).
   */
  const requestToggle = (flag: IFeatureFlag, nextEnabled: boolean) => {
    if (FeatureFlagHelper.isOwnedBy(flag, currentUserID)) {
      void toggleEnabled(flag, nextEnabled);
      return;
    }

    setPendingToggle({ flag, nextEnabled });
    setDialogToggle(Date.now());
  };

  const toggleEnabled = async (flag: IFeatureFlag, nextEnabled: boolean) => {
    // optimistic update; revert on failure
    setFlags((prev) =>
      prev.map((f) => (f._id === flag._id ? { ...f, enabled: nextEnabled } : f))
    );

    const result = await PlatformConsoleService.getInstance().updateFeatureFlag(
      flag._id,
      { enabled: nextEnabled }
    );

    if (!result?.success) {
      NotifyHelper.error({
        message_md: result?.error ?? 'Failed to update the feature flag.',
      });
      void refresh(environment);
    }
  };

  return (
    <ErrorBoundary identifier="FeatureFlagsSection">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
        <Flex align="center" justify="between" gap="3" wrap="wrap">
          <Flex direction="column" gap="1">
            <Heading size="5">Feature Flags</Heading>
            <Text size="1" color="gray">
              Runtime flags evaluated server-side, per environment.
            </Text>
          </Flex>

          <Flex align="center" gap="3">
            <Flex align="center" gap="2">
              <Text size="1" color="gray">
                Environment
              </Text>
              <Select.Root
                value={environment}
                onValueChange={(v) =>
                  setEnvironment(v as FeatureFlagEnvironment)
                }
              >
                <Select.Trigger />
                <Select.Content>
                  {MANAGEABLE_ENVIRONMENT_OPTIONS.map((o) => (
                    <Select.Item key={o.value} value={o.value}>
                      {o.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex align="center" gap="2">
              <Text size="1" color="gray">
                Owner
              </Text>
              <Select.Root value={ownerFilter} onValueChange={setOwnerFilter}>
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value={OWNER_FILTER_ALL}>All owners</Select.Item>
                  {ownerOptions.map((email) => (
                    <Select.Item key={email} value={email}>
                      {email}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex align="center" gap="2">
              <Text size="1" color="gray">
                My flags
              </Text>
              <Switch checked={myFlagsOnly} onCheckedChange={setMyFlagsOnly} />
            </Flex>

            <Button
              color={RADIX.COLOR.SUCCESS}
              disabled={loading}
              onClick={() =>
                navigate(
                  `${paths.platformConsole.featureFlagCreate}?environment=${environment}`
                )
              }
            >
              <PlusIcon />
              New Flag
            </Button>
          </Flex>
        </Flex>

        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Owner</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">
                Enabled
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Last toggled</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">
                Actions
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleFlags.map((flag) => (
              <Table.Row key={flag._id} align="center">
                <Table.RowHeaderCell>
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        paths.platformConsole.featureFlagDetail(flag._id)
                      );
                    }}
                  >
                    <Code color={RADIX.COLOR.NEUTRAL}>{flag.name}</Code>
                  </Link>
                </Table.RowHeaderCell>
                <Table.Cell>
                  <Text size="2">{flag.createdBy?.email ?? '—'}</Text>
                </Table.Cell>
                <Table.Cell align="center">
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={(checked) => requestToggle(flag, checked)}
                  />
                </Table.Cell>
                <Table.Cell>{daysSince(flag.lastToggledAt)}</Table.Cell>
                <Table.Cell>
                  {flag._created
                    ? lightFormat(parseISO(flag._created), LOCAL_DATE_FORMAT)
                    : '—'}
                </Table.Cell>
                <Table.Cell>
                  {flag._changed
                    ? lightFormat(parseISO(flag._changed), LOCAL_DATE_FORMAT)
                    : '—'}
                </Table.Cell>
                <Table.Cell align="center">
                  <Flex gap="2" justify="center">
                    <IconButton
                      variant="soft"
                      color={RADIX.COLOR.NEUTRAL}
                      title="History"
                      onClick={() =>
                        navigate(
                          `${paths.platformConsole.featureFlagDetail(flag._id)}?tab=history`
                        )
                      }
                    >
                      <CounterClockwiseClockIcon />
                    </IconButton>
                    {CAN_DUPLICATE && (
                      <IconButton
                        variant="soft"
                        color={RADIX.COLOR.NEUTRAL}
                        title="Duplicate to environment"
                        onClick={() => {
                          setSelectedFlag(flag);
                          setDialogDuplicate(Date.now());
                        }}
                      >
                        <CopyIcon />
                      </IconButton>
                    )}
                    <IconButton
                      variant="soft"
                      color={RADIX.COLOR.NEUTRAL}
                      title="Edit"
                      onClick={() =>
                        navigate(
                          paths.platformConsole.featureFlagDetail(flag._id)
                        )
                      }
                    >
                      <Pencil1Icon />
                    </IconButton>
                    <IconButton
                      variant="soft"
                      color={RADIX.COLOR.DANGER}
                      title="Delete"
                      onClick={() => {
                        setSelectedFlag(flag);
                        setDialogDelete(Date.now());
                      }}
                    >
                      <TrashIcon />
                    </IconButton>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}

            {!loading && visibleFlags.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <Box py="4">
                    <Text color="gray">
                      {flags.length === 0
                        ? `No feature flags in ${environment} yet.`
                        : 'No feature flags match the current filters.'}
                    </Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Flex>

      {dialogDuplicate && selectedFlag && (
        <FeatureFlagDuplicateDialog
          key={dialogDuplicate}
          flag={selectedFlag}
          onClose={(shouldRefresh) => {
            setDialogDuplicate(undefined);
            if (shouldRefresh) {
              void refresh(environment);
            }
          }}
        />
      )}

      {dialogToggle && pendingToggle && (
        <CommonConfirmationDialog
          key={dialogToggle}
          identifier="ConfirmToggleFeatureFlag"
          maxWidth={RADIX.DIALOG.WIDTH.MD}
          title="Modify Feature Flag"
          content={
            <Text>
              This flag is owned by{' '}
              <Code>
                {pendingToggle.flag.createdBy?.email ?? 'someone else'}
              </Code>
              . Are you sure you want to{' '}
              {pendingToggle.nextEnabled ? 'enable' : 'disable'} it?
            </Text>
          }
          action={{
            label: pendingToggle.nextEnabled ? 'Enable' : 'Disable',
            color: RADIX.COLOR.WARNING,
            onClick: async () => {
              await toggleEnabled(
                pendingToggle.flag,
                pendingToggle.nextEnabled
              );
            },
          }}
          onClose={() => {
            setDialogToggle(undefined);
            setPendingToggle(undefined);
          }}
        />
      )}

      {dialogDelete && selectedFlag && (
        <CommonConfirmationDialog
          key={dialogDelete}
          identifier="ConfirmDeleteFeatureFlag"
          maxWidth={RADIX.DIALOG.WIDTH.MD}
          title="Delete Feature Flag"
          content={
            <Flex direction="column" gap="2">
              <Text>
                This will permanently delete <Code>{selectedFlag.name}</Code>{' '}
                from the {selectedFlag.environment} environment. This cannot be
                undone.
              </Text>
              {!FeatureFlagHelper.isOwnedBy(selectedFlag, currentUserID) && (
                <Text color={RADIX.COLOR.WARNING}>
                  This flag is owned by{' '}
                  <Code>{selectedFlag.createdBy?.email ?? 'someone else'}</Code>
                  .
                </Text>
              )}
            </Flex>
          }
          action={{
            label: 'Delete',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              const result =
                await PlatformConsoleService.getInstance().deleteFeatureFlag(
                  selectedFlag._id
                );

              if (!result?.success) {
                NotifyHelper.error({
                  message_md:
                    result?.error ?? 'Failed to delete the feature flag.',
                });
                return;
              }

              NotifyHelper.success({
                message_md: `Feature flag \`${selectedFlag.name}\` deleted.`,
              });

              void refresh(environment);
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
